import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { TextField, Toggle } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { UnLocodeRow, searchUnLocode } from '../../services/ports.service';

/**
 * Bespoke edit/create drawer for Ports (cms.ports). Port / Country / Continent / Seaport
 * Code / Firms Code are open-text; Port, Country and Continent are required. UN/LOCODE is
 * a readonly code with an inline "Lookup" panel (V1 main.pl sub unlocode): it searches
 * cms.un_locations by name — seeded with the current Port value — and stores the picked
 * row's country+location code (e.g. USSAV), with an explicit Clear. Created is stamped on
 * insert and shown readonly.
 */
const TABLE = 'ports';

const s = (v: any): string => (v == null ? '' : String(v));

const PortsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [form, setForm] = useState<TablemaintRow>({
    pport: '', pcountry: '', pcontinent: '', seaportcode: '', firmscode: '', unlocode: '',
  });
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  // Inline UN/LOCODE lookup panel state.
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupTerm, setLookupTerm] = useState('');
  const [lookupRows, setLookupRows] = useState<UnLocodeRow[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const lookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  useEffect(() => {
    setForm({
      pport: s(record.pport),
      pcountry: s(record.pcountry),
      pcontinent: s(record.pcontinent),
      seaportcode: s(record.seaportcode),
      firmscode: s(record.firmscode),
      unlocode: s(record.unlocode),
    });
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // Debounced un_locations search (min 2 chars).
  const runLookup = (term: string) => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const q = term.trim();
    if (q.length < 2) { setLookupRows([]); setLookupLoading(false); return; }
    setLookupLoading(true);
    lookupTimer.current = setTimeout(async () => {
      try {
        setLookupRows(await searchUnLocode(q));
      } catch {
        setLookupRows([]);
      } finally {
        setLookupLoading(false);
      }
    }, 300);
  };

  // Open the lookup seeded with the current Port value (V1 "lookup based on the port").
  const openLookup = () => {
    const seed = s(form.pport);
    setLookupTerm(seed);
    setLookupOpen(true);
    runLookup(seed);
  };
  const pickLocode = (r: UnLocodeRow) => { setField('unlocode', r.code); setLookupOpen(false); };
  const clearLocode = () => { setField('unlocode', ''); setLookupOpen(false); };

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!s(form.pport).trim()) e.push('Port is required.');
    if (!s(form.pcountry).trim()) e.push('Country is required.');
    if (!s(form.pcontinent).trim()) e.push('Continent is required.');
    return e;
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const payload: TablemaintRow = {
      pport: s(form.pport).trim(),
      pcountry: s(form.pcountry).trim(),
      pcontinent: s(form.pcontinent).trim(),
      seaportcode: s(form.seaportcode).trim(),
      firmscode: s(form.firmscode).trim(),
      unlocode: s(form.unlocode).trim(),
    };
    try {
      if (isNew) {
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        (['pport', 'pcountry', 'pcontinent', 'seaportcode', 'firmscode', 'unlocode'] as const).forEach((k) => {
          if (s(form[k]).trim() !== s(record[k]).trim()) patch[k] = payload[k];
        });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save port.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isNew, form, record, formDeleted, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Port' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Port' : `Edit Port (#${recno})`;
  const createdDisplay = s(record.createdatetime);

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="52rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Port</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Port" required value={s(form.pport)} onChange={(v) => setField('pport', v)}
              disabled={isSaving} maxLength={50} invalid={showErrors && !s(form.pport).trim()} />
            <TextField label="Country" required value={s(form.pcountry)} onChange={(v) => setField('pcountry', v)}
              disabled={isSaving} maxLength={50} invalid={showErrors && !s(form.pcountry).trim()} />
            <TextField label="Continent" required value={s(form.pcontinent)} onChange={(v) => setField('pcontinent', v)}
              disabled={isSaving} maxLength={50} invalid={showErrors && !s(form.pcontinent).trim()} />
            <TextField label="Seaport Code" value={s(form.seaportcode)} onChange={(v) => setField('seaportcode', v)}
              disabled={isSaving} maxLength={50} />
            <TextField label="Firms Code" value={s(form.firmscode)} onChange={(v) => setField('firmscode', v)}
              disabled={isSaving} maxLength={50} />
          </div>
        </fieldset>

        {/* UN/LOCODE — readonly code + inline lookup (seeded by the Port value). */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">UN/LOCODE</legend>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-label mb-1">UN/LOCODE</label>
              <input readOnly value={s(form.unlocode)} placeholder="(none)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-body focus:outline-none" />
            </div>
            <button type="button" onClick={openLookup} disabled={isSaving}
              className="px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 inline-flex items-center gap-1.5">
              <MagnifyingGlassIcon className="w-4 h-4" /> Lookup
            </button>
            {s(form.unlocode) && (
              <button type="button" onClick={clearLocode} disabled={isSaving}
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50">
                Clear
              </button>
            )}
          </div>

          {lookupOpen && (
            <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3">
              <div className="flex items-center gap-2">
                <input autoFocus value={lookupTerm}
                  onChange={(e) => { setLookupTerm(e.target.value); runLookup(e.target.value); }}
                  placeholder="Search by port / city name…"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                <button type="button" onClick={() => setLookupOpen(false)}
                  className="px-3 py-2 text-sm font-medium text-label hover:text-gray-900 dark:hover:text-gray-100">
                  Close
                </button>
              </div>
              <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                {lookupLoading ? (
                  <div className="p-3 text-sm text-muted">Searching…</div>
                ) : lookupRows.length === 0 ? (
                  <div className="p-3 text-sm text-muted">
                    {lookupTerm.trim().length < 2 ? 'Type at least 2 characters.' : 'No matches.'}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold">Name</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Subdivision</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Country</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Location</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Function</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupRows.map((r, i) => (
                        <tr key={`${r.code}-${i}`} onClick={() => pickLocode(r)}
                          className="cursor-pointer border-t border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                          <td className="px-2 py-1.5 text-blue-700 dark:text-blue-300 font-medium">{titleCase(r.name)}</td>
                          <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">{r.subdivision}</td>
                          <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">{r.country}</td>
                          <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">{r.location} <span className="text-gray-400">({r.code})</span></td>
                          <td className="px-2 py-1.5 text-muted">{r.function}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </fieldset>

        {!isNew && createdDisplay && (
          <div className="text-xs text-muted">Created: {createdDisplay}</div>
        )}

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted}
              hint="Soft-deletes this port — hidden from filters but preserved in history. Uncheck and save to restore." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default PortsEditDrawer;
